import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  CheckCircle2,
  ChevronDown,
  RotateCcw,
  Target,
  XCircle,
} from 'lucide-react';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import type { TFunction } from 'i18next';
import { FormattedTextBlock } from './FormattedTextBlock';
import { CollapsibleFadePanel } from '../shared/CollapsibleFadePanel';
import { LearnerExpandableImage } from './LearnerExpandableImage';
import { QuestionType, type EnhancedQuizResultDto } from '../../services/contentService';
import { normalizeAppHostedUploadUrl } from '../../utils/normalizeAppHostedUploadUrl';

export interface QuizResultViewProps {
  quizResult: EnhancedQuizResultDto;
  tc: TFunction;
  passed: boolean;
  isTimeExpiryIncompleteFailure: boolean;
  isPlacementFlow: boolean;
  localizedGuidanceMessage?: string | null;
  questionResultsInAttemptOrder: Array<{
    questionId: number;
    isCorrect: boolean;
    userAnswer?: string | null;
  }>;
  activeQuestions: Array<Record<string, unknown>>;
  trophyAnimationError: boolean;
  errorAnimationError: boolean;
  trophyAnimationContainerRef: React.RefObject<HTMLDivElement | null>;
  errorAnimationContainerRef: React.RefObject<HTMLDivElement | null>;
  onRetake: () => void;
  onContinue: () => void;
  onPlacementNext?: () => void;
  onPlacementConsiderCurrent?: () => void;
  /** English-locked quiz: force LTR on question review bodies. */
  contentForceLtr?: boolean;
}

function scoreRingColor(passed: boolean, isTimeout: boolean): string {
  if (isTimeout) return 'text-red-500 dark:text-red-400';
  return passed ? 'text-emerald-500 dark:text-emerald-400' : 'text-rose-500 dark:text-rose-400';
}

function scoreRingTrack(passed: boolean, isTimeout: boolean): string {
  if (isTimeout) return 'text-red-100 dark:text-red-950/50';
  return passed ? 'text-emerald-100 dark:text-emerald-950/40' : 'text-rose-100 dark:text-rose-950/40';
}

export const QuizResultView: React.FC<QuizResultViewProps> = ({
  quizResult,
  tc,
  passed,
  isTimeExpiryIncompleteFailure,
  isPlacementFlow,
  localizedGuidanceMessage,
  questionResultsInAttemptOrder,
  activeQuestions,
  trophyAnimationError,
  errorAnimationError,
  trophyAnimationContainerRef,
  errorAnimationContainerRef,
  onRetake,
  onContinue,
  onPlacementNext,
  onPlacementConsiderCurrent,
  contentForceLtr = false,
}) => {
  const reviewDir = contentForceLtr ? 'ltr' : undefined;
  const formattedTextDir = contentForceLtr ? ('ltr' as const) : undefined;
  const [reviewOpen, setReviewOpen] = useState(!passed && !isTimeExpiryIncompleteFailure);
  const displayScore = isTimeExpiryIncompleteFailure ? 0 : quizResult.score;

  const statusLabel = isTimeExpiryIncompleteFailure
    ? tc('quiz.timesUp')
    : passed
      ? isPlacementFlow
        ? tc('quiz.passedShort')
        : tc('quiz.passed')
      : tc('quiz.failed');

  const headline = isTimeExpiryIncompleteFailure
    ? tc('quiz.timesUp')
    : passed
      ? isPlacementFlow
        ? tc('quiz.passedShort')
        : tc('quiz.congratulations')
      : tc('quiz.tryAgain');

  return (
    <motion.div className="mx-auto flex w-full max-w-2xl flex-col gap-5 pb-2" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
      <div
        className={`relative overflow-hidden rounded-2xl border px-4 py-6 text-center shadow-sm sm:px-8 sm:py-8 ${
          isTimeExpiryIncompleteFailure
            ? 'border-red-200/80 bg-gradient-to-b from-red-50 to-white dark:border-red-800/50 dark:from-red-950/35 dark:to-gray-900'
            : passed
              ? 'border-emerald-200/80 bg-gradient-to-b from-emerald-50 to-white dark:border-emerald-800/45 dark:from-emerald-950/30 dark:to-gray-900'
              : 'border-rose-200/80 bg-gradient-to-b from-rose-50 to-white dark:border-rose-800/45 dark:from-rose-950/30 dark:to-gray-900'
        }`}
      >
        <div
          className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-[radial-gradient(ellipse_at_top,rgba(255,255,255,0.65),transparent)] dark:bg-[radial-gradient(ellipse_at_top,rgba(255,255,255,0.06),transparent)]"
          aria-hidden
        />

        <div className="relative mx-auto mb-4 flex h-28 w-28 items-center justify-center sm:h-32 sm:w-32">
          <svg className="absolute inset-0 h-full w-full -rotate-90" viewBox="0 0 120 120" aria-hidden>
            <circle cx="60" cy="60" r="52" fill="none" strokeWidth="8" className={scoreRingTrack(passed, isTimeExpiryIncompleteFailure)} stroke="currentColor" />
            <circle
              cx="60"
              cy="60"
              r="52"
              fill="none"
              strokeWidth="8"
              strokeLinecap="round"
              className={scoreRingColor(passed, isTimeExpiryIncompleteFailure)}
              stroke="currentColor"
              strokeDasharray={`${Math.min(Math.max(displayScore, 0), 100) * 3.267} 326.7`}
            />
          </svg>
          <div className="relative flex flex-col items-center justify-center">
            <span className={`text-3xl font-bold tabular-nums sm:text-4xl ${scoreRingColor(passed, isTimeExpiryIncompleteFailure)}`}>
              {displayScore}%
            </span>
          </div>
        </div>

        <div className="relative mb-3 flex justify-center">
          <span
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide ${
              isTimeExpiryIncompleteFailure
                ? 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-100'
                : passed
                  ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/45 dark:text-emerald-100'
                  : 'bg-rose-100 text-rose-800 dark:bg-rose-900/45 dark:text-rose-100'
            }`}
          >
            {passed && !isTimeExpiryIncompleteFailure ? (
              <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
            ) : (
              <XCircle className="h-3.5 w-3.5" aria-hidden />
            )}
            {statusLabel}
          </span>
        </div>

        <h3 className="relative text-xl font-bold text-gray-900 dark:text-gray-50 sm:text-2xl">{headline}</h3>

        <p className="relative mt-2 text-sm text-gray-600 dark:text-gray-300">
          {isTimeExpiryIncompleteFailure
            ? tc('quiz.timesUpScoreZero')
            : tc('quiz.scoredSummary', { score: quizResult.score, required: quizResult.passingScore })}
        </p>

        {!isTimeExpiryIncompleteFailure && localizedGuidanceMessage ? (
          <p className="relative mx-auto mt-4 max-w-md rounded-xl border border-gray-200/80 bg-white/70 px-3 py-2.5 text-sm leading-relaxed text-gray-700 dark:border-gray-600/60 dark:bg-gray-800/50 dark:text-gray-200">
            {localizedGuidanceMessage}
          </p>
        ) : null}

        {!isTimeExpiryIncompleteFailure && passed && !isPlacementFlow ? (
          <div ref={trophyAnimationContainerRef} className="relative mx-auto mt-3 flex h-28 w-28 items-center justify-center sm:h-32 sm:w-32">
            {trophyAnimationError ? (
              <CheckCircle2 className="h-12 w-12 text-emerald-500 dark:text-emerald-400" aria-hidden />
            ) : (
              <DotLottieReact src="/animations/Trophy.lottie" loop autoplay className="h-full w-full" />
            )}
          </div>
        ) : null}

        {!isTimeExpiryIncompleteFailure && !passed ? (
          <div ref={errorAnimationContainerRef} className="relative mx-auto mt-3 flex h-20 w-20 items-center justify-center sm:h-24 sm:w-24">
            {errorAnimationError ? (
              <XCircle className="h-12 w-12 text-rose-500 dark:text-rose-400" aria-hidden />
            ) : (
              <DotLottieReact src="/animations/Error_Occurred.lottie" loop autoplay className="h-full w-full" />
            )}
          </div>
        ) : null}
      </div>

      {!isTimeExpiryIncompleteFailure && questionResultsInAttemptOrder.length > 0 ? (
        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-900/80">
          <button
            type="button"
            onClick={() => setReviewOpen((v) => !v)}
            className="flex w-full items-center justify-between gap-3 px-4 py-3 text-start transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/60"
            aria-expanded={reviewOpen}
          >
            <span className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
              <Target className="h-4 w-4 text-violet-600 dark:text-violet-400" aria-hidden />
              {tc('quiz.questionReview')}
            </span>
            <ChevronDown
              className={`h-4 w-4 shrink-0 text-gray-500 transition-transform duration-200 ease-out dark:text-gray-400 ${reviewOpen ? 'rotate-180' : ''}`}
              aria-hidden
            />
          </button>
          <CollapsibleFadePanel expanded={reviewOpen}>
              <div className="space-y-2 border-t border-gray-100 px-3 py-3 dark:border-gray-800 sm:px-4">
                {questionResultsInAttemptOrder.map((qr) => {
                  const question = activeQuestions.find((q: any) => q.id === qr.questionId);
                  if (!question) return null;
                  return (
                    <div
                      key={qr.questionId}
                      dir={reviewDir}
                      className={`rounded-xl border px-3 py-3 sm:px-4 ${
                        contentForceLtr ? 'text-start' : ''
                      } ${
                        qr.isCorrect
                          ? 'border-emerald-200/90 bg-emerald-50/70 dark:border-emerald-800/50 dark:bg-emerald-950/20'
                          : 'border-rose-200/90 bg-rose-50/70 dark:border-rose-800/50 dark:bg-rose-950/20'
                      }`}
                    >
                      <div className="flex items-start gap-2.5">
                        {qr.isCorrect ? (
                          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
                        ) : (
                          <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-rose-600 dark:text-rose-400" />
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {(question as any).textContent?.trim() ? (
                              <FormattedTextBlock
                                textContent={(question as any).textContent}
                                formatting={
                                  (question as any).textFormatting
                                    ? typeof (question as any).textFormatting === 'string'
                                      ? JSON.parse((question as any).textFormatting)
                                      : (question as any).textFormatting
                                    : undefined
                                }
                                contentDirection={formattedTextDir}
                              />
                            ) : (
                              (question as any).question
                            )}
                          </p>
                          <div className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                            <span>{tc('quiz.yourAnswer')} </span>
                            {renderUserAnswer(qr, question as any, tc)}
                          </div>
                          {(question as any).explanation ? (
                            <p
                              dir={reviewDir}
                              className={`mt-2 text-xs italic text-gray-600 dark:text-gray-400${contentForceLtr ? ' text-start' : ''}`}
                            >
                              {(question as any).explanation}
                            </p>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
          </CollapsibleFadePanel>
        </div>
      ) : null}

      {isPlacementFlow ? (
        passed ? (
          <div className="rounded-2xl border border-violet-200 bg-violet-50/80 p-4 dark:border-violet-800/50 dark:bg-violet-950/25">
            <p className="text-sm font-semibold text-violet-900 dark:text-violet-100">{tc('quiz.placementNextStep')}</p>
            <p className="mt-1 text-sm text-violet-800 dark:text-violet-200">{tc('quiz.placementPassedBody')}</p>
            <div className="mt-4 flex flex-col gap-2 sm:flex-row">
              <button
                type="button"
                onClick={onPlacementNext}
                className="flex-1 rounded-xl bg-violet-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-violet-700"
              >
                {tc('quiz.nextQuizButton')}
              </button>
              <button
                type="button"
                onClick={onPlacementConsiderCurrent}
                className="flex-1 rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700"
              >
                {tc('quiz.considerThisQuiz')}
              </button>
            </div>
          </div>
        ) : (
          <div className="rounded-2xl border border-amber-200 bg-amber-50/80 p-4 text-sm text-amber-900 dark:border-amber-800/50 dark:bg-amber-950/25 dark:text-amber-100">
            {tc('quiz.returningToPlacement')}
          </div>
        )
      ) : (
        <div className="sticky bottom-0 -mx-1 flex flex-col gap-2 border-t border-gray-200/80 bg-white/95 px-1 pt-3 backdrop-blur-sm dark:border-gray-700/80 dark:bg-gray-900/95 sm:static sm:border-0 sm:bg-transparent sm:px-0 sm:pt-0 sm:backdrop-blur-none">
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-center">
            {!passed && !isTimeExpiryIncompleteFailure ? (
              <button
                type="button"
                onClick={onRetake}
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-300 bg-white px-5 py-2.5 text-sm font-semibold text-gray-800 transition hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
              >
                <RotateCcw className="h-4 w-4" aria-hidden />
                {tc('quiz.retake')}
              </button>
            ) : null}
            <button
              type="button"
              onClick={onContinue}
              className={`inline-flex items-center justify-center gap-2 rounded-xl px-5 py-2.5 text-sm font-semibold text-white shadow-md transition ${
                passed
                  ? 'bg-emerald-600 hover:bg-emerald-700 dark:bg-emerald-600 dark:hover:bg-emerald-500'
                  : 'bg-violet-600 hover:bg-violet-700 dark:bg-violet-600 dark:hover:bg-violet-500'
              }`}
            >
              {passed ? tc('quiz.continue') : tc('quiz.reviewContent')}
            </button>
          </div>
        </div>
      )}
    </motion.div>
  );
};

function getOptionText(opt: any): string {
  const block = opt?.blocks?.find((b: any) => b.type === 'text');
  if (block?.textContent) {
    if (typeof document !== 'undefined') {
      const temp = document.createElement('div');
      temp.innerHTML = block.textContent;
      return temp.textContent || temp.innerText || opt?.textContent || '';
    }
    return opt?.textContent || '';
  }
  return opt?.textContent || '';
}

function isQuestionUnanswered(qr: { userAnswer?: string | null }): boolean {
  return qr.userAnswer == null || String(qr.userAnswer).trim() === '';
}

function unansweredLabel(tc: TFunction): string {
  return tc('quiz.notAnsweredToQuestion', { defaultValue: tc('quiz.noAnswer') });
}

function renderUserAnswer(
  qr: { userAnswer?: string | null },
  question: any,
  tc: TFunction
): React.ReactNode {
  const textAnswer = formatUserAnswer(qr, question, tc);
  const unanswered = unansweredLabel(tc);

  if (question.questionOptions?.length && qr.userAnswer != null && String(qr.userAnswer).trim() !== '') {
    const id = parseInt(String(qr.userAnswer).trim(), 10);
    if (!Number.isNaN(id)) {
      const opt = question.questionOptions.find((o: any) => o.id === id);
      const imageUrl = opt?.imageUrl?.trim();
      if (imageUrl) {
        return (
          <span className="mt-1.5 block max-w-full overflow-hidden rounded-lg border border-gray-200 bg-white p-1.5 dark:border-gray-600 dark:bg-gray-900/40">
            <LearnerExpandableImage
              src={normalizeAppHostedUploadUrl(imageUrl)}
              alt=""
              className="mx-auto max-h-28 w-full max-w-full object-contain"
            />
            {textAnswer && textAnswer !== unanswered ? (
              <span className="mt-1 block text-center text-[11px] font-medium text-gray-700 dark:text-gray-300">
                {textAnswer}
              </span>
            ) : null}
          </span>
        );
      }
    }
  }

  return <span className="font-medium text-gray-800 dark:text-gray-200">{textAnswer}</span>;
}

function formatUserAnswer(
  qr: { userAnswer?: string | null },
  question: any,
  tc: TFunction
): string {
  const unanswered = unansweredLabel(tc);

  if (isQuestionUnanswered(qr)) {
    return unanswered;
  }

  if (
    (question as any).questionType === QuestionType.Puzzle ||
    (question as any).questionType === QuestionType.SortParagraphs
  ) {
    const userOrder = qr.userAnswer!
      .split(',')
      .map((id) => parseInt(id.trim(), 10))
      .filter((id) => !Number.isNaN(id));
    if (userOrder.length > 0 && question.questionOptions) {
      const texts = userOrder
        .map((optId) => {
          const opt = question.questionOptions?.find((o: any) => o.id === optId);
          return opt ? getOptionText(opt) || `[${optId}]` : `[${optId}]`;
        })
        .filter(Boolean);
      return texts.length > 0 ? texts.join(' · ') : unanswered;
    }
    return qr.userAnswer || unanswered;
  }
  if ((question as any).questionType === QuestionType.FindTheWords) {
    const ids = qr.userAnswer!
      .split(',')
      .map((id) => parseInt(id.trim(), 10))
      .filter((id) => !Number.isNaN(id));
    if (ids.length > 0 && question.questionOptions) {
      const texts = ids
        .map((optId) => {
          const opt = question.questionOptions?.find((o: any) => o.id === optId);
          return opt ? getOptionText(opt) || `[${optId}]` : null;
        })
        .filter(Boolean);
      if (texts.length > 0) return texts.join(', ');
    }
    return qr.userAnswer || unanswered;
  }
  if ((question as any).questionType === QuestionType.Essay) {
    return qr.userAnswer || unanswered;
  }
  if ((question as any).questionType === QuestionType.Crossword) {
    const raw = qr.userAnswer || '';
    const letters = raw.replace(/[#.\s]/g, '');
    return letters.length > 0 ? raw : unanswered;
  }
  if (question.questionOptions?.length) {
    const id = parseInt(String(qr.userAnswer).trim(), 10);
    if (!Number.isNaN(id)) {
      const opt = question.questionOptions.find((o: any) => o.id === id);
      if (opt) return getOptionText(opt) || qr.userAnswer!;
    }
  }
  return qr.userAnswer ?? unanswered;
}
